import React from 'react';
import { ChakraProvider, PortalManager, extendTheme } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

export const portalContainerRef = {
  current: typeof document !== 'undefined' ? document.getElementById('portal-root') : null,
} as { current: HTMLElement | null };

const sharedPortalProps = { containerRef: portalContainerRef };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1 * 60 * 1000, // 1 minute - data is fresh for 1 minute
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      refetchOnWindowFocus: true, // Auto-refetch when user returns to tab
      refetchOnMount: true, // Refetch on component mount to ensure fresh data
      retry: (failureCount, error: any) => {
        // Authentication, permission and missing-resource errors need user action.
        if ([401, 403, 404].includes(error?.status)) return false;
        if (error?.message?.includes('USER_BANNED')) return false;
        // Retry transient failures once.
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => {
        // Small delay before retry to allow token refresh to complete
        return Math.min(1000 * Math.pow(2, attemptIndex), 2000);
      },
    },
  },
});

const theme = extendTheme({
  config: { initialColorMode: 'light', useSystemColorMode: false },
  components: {
    Modal: { defaultProps: { portalProps: sharedPortalProps } },
    Drawer: { defaultProps: { portalProps: sharedPortalProps } },
    Menu: { defaultProps: { portalProps: sharedPortalProps } },
    Popover: { defaultProps: { portalProps: sharedPortalProps } },
    Tooltip: { defaultProps: { portalProps: sharedPortalProps } },
  },
});

export function AppProviders({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <ChakraProvider theme={theme}>
      <PortalManager zIndex={1500}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </PortalManager>
    </ChakraProvider>
  );
}
